<template>
	<div class="editor">
		<table class="simple-table">
			<tr v-if="props.itemId">
				<th>번호 / 상태</th>
				<td>
					<p v-html="idAndStatus" />
				</td>
			</tr>
			<tr v-if="props.itemId">
				<th>등록일시</th>
				<td>{{ createdAt }}</td>
			</tr>
			<tr>
				<th>카테고리</th>
				<td>
					<single-category-selector
						v-if="isEditable"
						v-model="categoryIds"
						@mounted="actions.onCategorySelectorMounted"
					/>
					<p v-else>{{ categoryString }}</p>
				</td>
			</tr>
			<tr v-if="props.itemId">
				<th>브랜드명</th>
				<td>
					{{ product.brandTitle }}
				</td>
			</tr>
			<tr>
				<th>제품명</th>
				<td>
					<search-product-by-category
						v-if="isEditable"
						v-model="product"
						:category-ids="categoryIds"
					/>
					<p v-else>{{ product.productTitle }}</p>
				</td>
			</tr>
			<tr>
				<th>노출기간</th>
				<td>
					<form
						id="editor-product-date-range"
						@submit.prevent="actions.onSubmit"
					>
						<DateRange v-model="datePeriod" type="datetime-local" />
					</form>
				</td>
			</tr>
		</table>
		<div class="btn-wrap-main">
			<button
				form="editor-product-date-range"
				type="submit"
				class="btn-main blue"
				v-text="itemId ? '수정하기' : '추천제품 등록'"
			/>
			<button
				type="button"
				@click="actions.onCancel"
				class="btn-main blue bordered"
				v-text="'취소'"
			/>
		</div>
	</div>
</template>
<script>
	import {
		defineComponent,
		toRefs,
		reactive,
		computed,
		onMounted,
	} from '@vue/composition-api';
	import {
		fromFormat,
		startOf,
		endOf,
		format,
	} from '@/assets/scripts/datetime';
	import fp from 'lodash/fp';
	import compact from 'lodash/compact';
	import match from 'pyonkoro.match';
	import validator from '@/assets/scripts/validator';
	import SingleCategorySelector from '@/components/SingleCategorySelector';
	import SearchProductByCategory from '@/components/SearchProductByCategory';
	import DateRange from '@/components/DateRange';

	export default defineComponent({
		name: 'EditorProductRegister',
		components: {
			SingleCategorySelector,
			SearchProductByCategory,
			DateRange,
		},
		props: {
			itemId: {
				type: [String, Number],
				default: null,
			},
		},
		setup(props, { root }) {
			const { $store: store, $router: router, $axiosAdmin2 } = root;
			const state = reactive({
				status: null,
				categoryIds: {
					idFirstCategory: null,
					idSecondCategory: null,
					idThirdCategory: null,
					thirdCategoryLength: 0,
				},
				categoryText: {
					first: '',
					second: '',
					third: '',
				},
				product: {
					idProduct: null,
					productTitle: '',
					brandTitle: '',
				},
				createdAt: null,
				brandTitle: null,
				datePeriod: { from: '', to: '' },
				idAndStatus: computed(() => {
					const template = (string, className) =>
						`<span class=\"editor__status ${className}\">${string}</span>`;
					return [
						props.itemId,
						match(
							[
								[
									s => s === 'open',
									() => [
										template(
											'전시중',
											'editor__status--blue'
										),
										template(
											'편집불가',
											'editor__status--red'
										),
									],
								],
								[
									s => s === 'ready',
									() => [
										template(
											'전시예정',
											'editor__status--green'
										),
										template(
											'편집가능',
											'editor__status--blue'
										),
									],
								],
								[
									s => s === 'closed',
									() => [
										template(
											'비전시',
											'editor__status--red'
										),
										template(
											'편집가능',
											'editor__status--blue'
										),
									],
								],
							],
							[],
							state.status
						).join(''),
					].join(' / ');
				}),
				categoryString: computed(() =>
					compact([
						state.categoryText.first.trim(),
						state.categoryText.second.trim(),
						state.categoryText.third.trim(),
					]).join(' > ')
				),
				isEditable: computed(
					() =>
						!props.itemId ||
						(state.status && state.status !== 'open')
				),
			});

			const mutations = {
				messageAlert: v => {
					if (!v.result) {
						alert(v.message);
					}
					return v;
				},
				validation: () => {
					return fp.flow(
						validator([
							[
								({ categoryIds }) =>
									!categoryIds.idFirstCategory,
								'1차 카테고리를 선택해 주세요',
							],
							[
								({ categoryIds }) =>
									!categoryIds.idSecondCategory,
								'2차 카테고리를 선택해 주세요',
							],
							[
								({ categoryIds }) =>
									!categoryIds.idThirdCategory &&
									categoryIds.thirdCategoryLength > 0,
								'3차 카테고리를 선택해 주세요',
							],
							[
								({ product }) => !product.idProduct,
								'추천 노출될 제품을 선택해 주세요',
							],
							[
								({ datePeriod }) => !datePeriod.from,
								'노출 시작일을 선택해 주세요',
							],
							[
								({ datePeriod }) => !datePeriod.to,
								'노출 종료일을 선택해 주세요',
							],
							[
								({ datePeriod }) =>
									datePeriod.from > datePeriod.to,
								'종료일이 시작일보다 빠를 수 없습니다',
							],
						]),
						mutations.messageAlert
					)(state);
				},
			};

			const actions = {
				onSubmit: () => {
					if (
						mutations.validation() &&
						confirm(
							`추천제품을 ${props.itemId ? '수정' : '등록'} 하시겠습니까?`
						)
					) {
						const idRegister =
							process.env.NODE_ENV === 'development' &&
							store.state.login.idRegister
								? 636104
								: store.state.login.idRegister;
						const {
							idFirstCategory: firstCategoryV2Id,
							idSecondCategory: secondCategoryV2Id,
							idThirdCategory: thirdCategoryV2Id,
						} = state.categoryIds;

						const params = {
							idRegister,
							firstCategoryV2Id,
							secondCategoryV2Id,
							thirdCategoryV2Id:
								thirdCategoryV2Id === 'all'
									? null
									: thirdCategoryV2Id,
							showMergedChildren: thirdCategoryV2Id === 'all',
							idProduct: state.product.idProduct,
							startOn: fp.flow(
								fromFormat('YYYY-MM-DDTHH:mm'),
								startOf('minute'),
								format('x'),
								Number
							)(state.datePeriod.from),
							endOn: fp.flow(
								fromFormat('YYYY-MM-DDTHH:mm'),
								endOf('minute'),
								startOf('second'),
								format('x'),
								Number
							)(state.datePeriod.to),
						};

						if (props.itemId) {
							params.id = props.itemId;
						}

						$axiosAdmin2
							.post(`/api/hotediter`, params)
							.then(() => {
								alert(
									props.itemId
										? '수정되었습니다'
										: '등록되었습니다'
								);
								actions.onCancel();
							})
							.catch(({ response }) => {
								if (response.status === 451) {
									alert(response.data.message);
								} else {
									alert('등록중 오류가 발생하였습니다');
								}
								console.error(response);
							});
					}
				},
				onCancel: () => {
					router.push('/advertisement/editor-product/list');
				},
				onCategorySelectorMounted: async setCategories => {
					if (props.itemId) {
						setCategories();
					}
				},
			};

			onMounted(async () => {
				if (props.itemId) {
					$axiosAdmin2
						.get(`/api/hotediter/${props.itemId}`)
						.then(({ data }) => {
							const dateFormat = 'YYYY-MM-DDTHH:mm';
							state.categoryIds = {
								idFirstCategory: data.firstCategoryV2Id,
								idSecondCategory: data.secondCategoryV2Id,
								idThirdCategory: data.thirdCategoryV2Id,
								thirdCategoryLength: 0,
							};

							state.status = data.status;
							state.product = {
								idProduct: data.productId,
								productTitle: data.productTitle,
								brandTitle: data.brandTitle,
							};
							state.categoryText = {
								first: data.firstCategoryText,
								second: data.secondCategoryText,
								third: data.thirdCategoryText,
							};
							state.createdAt = fp.flow(
								fromFormat('X'),
								format('YYYY-MM-DD HH:mm:ss')
							)(data.createdAt);
							state.datePeriod = {
								from: fp.flow(
									fromFormat('X'),
									format(dateFormat)
								)(data.startOn),
								to: fp.flow(
									fromFormat('X'),
									format(dateFormat)
								)(data.endOn),
							};
						})
						.catch(({ response }) => {
							alert('데이터를 불러오는 중 오류가 발생했습니다.');
							console.error(response);
						});
				}
			});

			return {
				...toRefs(state),
				props,
				actions,
			};
		},
	});
</script>
<style lang="scss">
	.editor {
		padding-top: 24px;
	}

	.editor__status {
		display: inline-block;
		padding: 0.25em 0.4em;
		margin: 0 0.4em;
		background: #ffffff;
		border: 1px solid #000;
		border-radius: 1em;
		color: #000;
		line-height: 13px;
	}

	.editor__status--blue {
		color: $font-blue;
		border-color: $font-blue;
	}

	.editor__status--green {
		color: $green;
		border-color: $green;
	}

	.editor__status--red {
		color: $red;
		border-color: $red;
	}
</style>
